import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useGameStore } from '@/store/gameStore';
import { fetchChinaProvincesData, isPointInGeometry, calculateDistance, getProvinceCenter } from '@/services/mapService';
import { findMatchingProvince, normalizeProvinceName } from '@/utils/provinceNameMatcher';
import { getProvinceTolerance } from '@/config/provinceSizes';
import { DragDropHint } from './DragDropHint';
import { AccuracyIndicator, calculateAccuracy, type AccuracyLevel } from './AccuracyIndicator';
import { useResponsiveMap } from '@/hooks/useResponsiveMap';
import { useContainerSize } from '@/hooks/useContainerSize';
import { checkMapBounds, autoAdjustMapToFit, validateMapConfig } from '@/utils/mapBoundsUtils';
// 移除了MapZoomControls和ZoomIndicator导入，因为不再需要缩放功能
import { IsolatedEChartsContainer } from './IsolatedEChartsContainer';

interface EChartsMapProps {
  hintedProvinceId?: string | null;
  onProvinceClick?: (provinceId: string) => void;
}

export const EChartsMapSimple: React.FC<EChartsMapProps> = ({
  hintedProvinceId = null,
  onProvinceClick
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const isUpdatingColors = useRef<boolean>(false); // 防止颜色更新期间的冲突
  const isDragging = useRef<boolean>(false); // 防止拖拽期间的resize冲突
  const [mapData, setMapData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false); // 添加地图就绪状态
  const [mapConfigLocked, setMapConfigLocked] = useState(false); // 地图配置锁定状态
  const lockedMapConfig = useRef<any>(null); // 锁定的地图配置
  const [dragHint, setDragHint] = useState<{
    show: boolean;
    message: string;
    type: 'info' | 'success' | 'error' | 'warning';
  }>({
    show: false,
    message: '',
    type: 'info'
  });
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  
  // 监听容器尺寸
  const containerSize = useContainerSize(chartRef);
  
  // 使用容器尺寸的响应式配置
  const { 
    containerHeight, 
    zoom, 
    center, 
    layoutSize, 
    layoutCenter, 
    fontSize, 
    borderWidth 
  } = useResponsiveMap(containerSize);
  
  const [accuracyIndicator, setAccuracyIndicator] = useState<{
    show: boolean;
    accuracy: AccuracyLevel;
    distance?: number;
    provinceName?: string;
  }>({
    show: false,
    accuracy: 'perfect'
  });
  
  const { 
    provinces, 
    completedProvinces, 
    isGameStarted,
    showProvinceNames, // 添加省名显示状态
    placeProvince,
    continueChallenge,
    backToHome
    // 移除了地图拖拽和缩放相关的状态和函数
  } = useGameStore();

  // 地图配置锁定函数 - 锁定autoAdjustMapToFit完成后的最终配置
  const lockMapConfiguration = () => {
    if (chartInstance.current && !mapConfigLocked) {
      const currentOption = chartInstance.current.getOption();
      const geoConfig = currentOption.geo?.[0];
      
      if (geoConfig) {
        // 获取当前的实际配置（包括autoAdjustMapToFit调整后的值）
        const finalConfig = {
          zoom: geoConfig.zoom,
          center: geoConfig.center,
          layoutSize: geoConfig.layoutSize,
          layoutCenter: geoConfig.layoutCenter,
          roam: geoConfig.roam,
          // 保存完整的geo配置
          fullGeoConfig: { ...geoConfig }
        };
        
        lockedMapConfig.current = finalConfig;
        setMapConfigLocked(true);
        
        console.log('🔒 地图配置已锁定（自动调整后的最终配置）:', {
          zoom: finalConfig.zoom,
          center: finalConfig.center,
          layoutSize: finalConfig.layoutSize,
          layoutCenter: finalConfig.layoutCenter,
          timestamp: new Date().toLocaleTimeString()
        });
        
        // 验证锁定的配置是否合理
        if (finalConfig.layoutSize) {
          const sizeValue = parseFloat(finalConfig.layoutSize.toString().replace('%', ''));
          console.log(`📏 锁定的layoutSize: ${finalConfig.layoutSize} (${sizeValue}%)`);
        }
        
        if (finalConfig.zoom) {
          console.log(`🔍 锁定的zoom: ${finalConfig.zoom}`);
        }
      } else {
        console.warn('⚠️ 无法获取geo配置，锁定失败');
      }
    } else if (mapConfigLocked) {
      console.log('ℹ️ 地图配置已经锁定，跳过重复锁定');
    } else {
      console.warn('⚠️ chartInstance不存在，无法锁定配置');
    }
  };

  // 智能resize函数 - 保护地图配置不被意外改变
  const smartResize = (preserveMapConfig = true, reason = '未知原因') => {
    if (!chartInstance.current) {
      console.log('⚠️ smartResize跳过：chartInstance不存在');
      return;
    }
    
    console.log(`🔄 执行smartResize，原因: ${reason}, 保护配置: ${preserveMapConfig}`);
    
    if (preserveMapConfig && mapConfigLocked && lockedMapConfig.current) {
      console.log('🔒 执行保护性resize，保持地图配置不变');
      
      // 先执行resize调整画布大小
      chartInstance.current.resize();
      
      // 延迟恢复锁定的地图配置，防止resize改变地图大小
      setTimeout(() => {
        if (chartInstance.current && lockedMapConfig.current && mapConfigLocked) {
          try {
            // 恢复锁定的地图配置
            chartInstance.current.setOption({
              geo: {
                map: 'china',
                roam: false,
                zoom: lockedMapConfig.current.zoom,
                center: lockedMapConfig.current.center,
                layoutSize: lockedMapConfig.current.layoutSize,
                layoutCenter: lockedMapConfig.current.layoutCenter,
                // 保持其他配置不变
                itemStyle: lockedMapConfig.current.fullGeoConfig?.itemStyle,
                emphasis: lockedMapConfig.current.fullGeoConfig?.emphasis,
                label: lockedMapConfig.current.fullGeoConfig?.label
              }
            }, false); // 不合并，直接设置
            
            console.log('✅ 地图配置已恢复到锁定状态');
          } catch (error) {
            console.error('❌ 恢复地图配置失败:', error);
          }
        }
      }, 100); // 延迟100ms确保resize完成
    } else {
      // 正常resize（初始化阶段或明确要求不保护配置）
      console.log('🔄 执行正常resize');
      chartInstance.current.resize();
      
      // 如果是初始化阶段，完成后尝试锁定配置
      if (!mapConfigLocked && reason.includes('初始化')) {
        setTimeout(() => {
          lockMapConfiguration();
        }, 200);
      }
    }
  };

  // 确保组件完全挂载
  useEffect(() => {
    console.log('📦 EChartsMapSimple组件挂载');
    // 使用setTimeout确保DOM完全渲染
    const timer = setTimeout(() => {
      setIsComponentMounted(true);
      console.log('✅ 组件挂载状态设置完成');
    }, 100);
    
    return () => {
      console.log('📦 EChartsMapSimple组件开始卸载');
      clearTimeout(timer);
      setIsComponentMounted(false);
      setIsMapReady(false); // 重置地图就绪状态
      setMapConfigLocked(false); // 重置地图配置锁定状态
      lockedMapConfig.current = null; // 清除锁定配置
      
      // 安全清理ECharts实例
      if (chartInstance.current) {
        try {
          console.log('🧹 安全清理ECharts实例');
          chartInstance.current.dispose();
          chartInstance.current = null;
          console.log('✅ ECharts实例清理完成');
        } catch (error) {
          console.warn('⚠️ ECharts实例清理时出现警告:', error);
          chartInstance.current = null;
        }
      }
      
      console.log('📦 EChartsMapSimple组件卸载完成');
    };
  }, []);

  // 监听DOM元素准备状态
  useEffect(() => {
    const checkDOMReady = () => {
      if (chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        console.log('✅ DOM元素已准备好:', {
          width: chartRef.current.offsetWidth,
          height: chartRef.current.offsetHeight,
          hasParent: !!chartRef.current.parentElement,
          rect: { width: rect.width, height: rect.height },
          isVisible: rect.width > 0 && rect.height > 0
        });
        return true;
      }
      return false;
    };

    // 立即检查
    if (checkDOMReady()) {
      return;
    }

    // 如果DOM还没准备好，使用轮询检查
    console.log('⏳ DOM元素还未准备好，开始轮询检查...');
    const pollInterval = setInterval(() => {
      if (checkDOMReady()) {
        console.log('🎯 DOM元素轮询检查成功');
        clearInterval(pollInterval);
        
        // DOM准备好后，如果有地图数据就立即尝试初始化
        if (mapData && !isLoading && !chartInstance.current) {
          console.log('🚀 DOM准备好后立即尝试地图初始化');
          setTimeout(() => {
            if (chartRef.current && mapData && !isLoading && !chartInstance.current) {
              // 手动触发地图初始化
              initializeMapNow();
            }
          }, 100);
        }
      }
    }, 100);

    // 5秒后停止轮询
    const timeout = setTimeout(() => {
      console.warn('⚠️ DOM元素准备超时');
      clearInterval(pollInterval);
    }, 5000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [mapData, isLoading]);

  // 独立的地图初始化函数
  const initializeMapNow = async () => {
    if (!chartRef.current || !mapData || chartInstance.current) {
      console.log('❌ 无法初始化地图:', {
        hasChartRef: !!chartRef.current,
        hasMapData: !!mapData,
        hasInstance: !!chartInstance.current
      });
      return;
    }

    try {
      console.log('🚀 立即初始化地图...');
      
      // 注册地图数据
      echarts.registerMap('china', mapData);
      
      // 创建图表实例
      chartInstance.current = echarts.init(chartRef.current);
      
      if (!chartInstance.current) {
        throw new Error('ECharts实例创建失败');
      }

      // 基础配置 - 重要：geo不设置itemStyle，让series完全控制颜色
      const option = {
        geo: {
          map: 'china',
          roam: false, // 完全禁用拖拽和缩放
          // 使用layoutCenter和layoutSize确保完整显示
          layoutCenter: layoutCenter,
          layoutSize: layoutSize,
          // 备用：使用zoom和center（当layoutSize不生效时）
          zoom: zoom,
          center: center,
          // 重要：不设置itemStyle，让series控制所有颜色
          label: { show: false }
        },
        series: [{
          type: 'map',
          map: 'china',
          geoIndex: 0,
          data: mapData.features.map((feature: any) => ({
            name: feature.properties.name,
            value: 0,
            itemStyle: {
              areaColor: '#e3f2fd',
              borderColor: '#2196f3',
              borderWidth: 1
            }
          })),
          itemStyle: {
            borderColor: '#2196f3',
            borderWidth: 1
          },
          label: { show: false }
        }]
      };

      chartInstance.current.setOption(option);
      console.log('✅ 立即地图初始化成功');
      
      // 设置地图就绪状态
      setIsMapReady(true);
      console.log('✅ 立即初始化完成，设置就绪状态');
      
      // 等待可能的自动调整完成后再锁定配置
      setTimeout(async () => {
        if (chartInstance.current) {
          console.log('🔍 立即初始化后检查边界...');
          const isInBounds = checkMapBounds(chartInstance.current);
          
          if (!isInBounds) {
            console.log('⚠️ 立即初始化后需要自动调整...');
            const adjusted = await autoAdjustMapToFit(chartInstance.current);
            if (adjusted) {
              console.log('✅ 立即初始化后自动调整完成');
            }
          }
          
          // 无论是否调整，都锁定最终配置
          setTimeout(() => {
            lockMapConfiguration();
            console.log('🔒 立即初始化流程完成，配置已锁定');
          }, 200);
        }
      }, 600);
      
      // 延迟进行边界检测和自动调整
      setTimeout(async () => {
        if (chartInstance.current) {
          console.log('🔍 开始地图边界检测...');
          const isInBounds = checkMapBounds(chartInstance.current);
          
          if (!isInBounds) {
            console.log('⚠️ 地图超出边界，开始自动调整...');
            const adjusted = await autoAdjustMapToFit(chartInstance.current);
            if (adjusted) {
              console.log('✅ 地图自动调整完成');
              
              // 🔑 关键修复：在自动调整完成后锁定配置
              setTimeout(() => {
                lockMapConfiguration();
                console.log('🔒 自动调整完成后，地图配置已锁定');
              }, 200);
            } else {
              console.warn('❌ 地图自动调整失败');
              // 即使调整失败，也尝试锁定当前配置
              setTimeout(() => {
                lockMapConfiguration();
                console.log('🔒 调整失败后，锁定当前地图配置');
              }, 200);
            }
          } else {
            console.log('✅ 地图已在边界内');
            
            // 🔑 关键修复：如果地图已在边界内，直接锁定配置
            setTimeout(() => {
              lockMapConfiguration();
              console.log('🔒 地图边界检测通过，配置已锁定');
            }, 200);
          }
        }
      }, 500);
      
      // 强制刷新
      setTimeout(() => {
        if (chartInstance.current) {
          smartResize(false, '立即初始化完成'); // 初始化阶段不保护配置
        }
      }, 100);
      
    } catch (err) {
      console.error('❌ 立即地图初始化失败:', err);
    }
  };

  // 地图现在完全固定，移除边界计算函数
  // calculateMapBoundary 函数已移除，因为不再需要拖拽边界限制

  // 加载地图数据
  useEffect(() => {
    console.log('🚀 开始加载地图数据...');
    
    const loadMapData = async () => {
      try {
        console.log('📡 设置加载状态...');
        setIsLoading(true);
        setError(null);
        
        console.log('📥 调用fetchChinaProvincesData...');
        const data = await fetchChinaProvincesData();
        console.log('🗺️ 地图数据加载完成，包含', data?.features?.length, '个省份');
        console.log('📊 地图数据样本:', {
          type: data?.type,
          featuresCount: data?.features?.length,
          firstFeature: data?.features?.[0]?.properties?.name
        });
        
        console.log('💾 设置地图数据到状态...');
        setMapData(data);
        console.log('✅ 地图数据状态设置完成');
      } catch (err) {
        console.error('❌ 加载地图数据失败:', err);
        setError('加载地图数据失败，请检查网络连接');
      } finally {
        console.log('🏁 设置加载完成状态...');
        setIsLoading(false);
        console.log('✅ 地图数据加载流程完成');
      }
    };

    loadMapData();
  }, []);

  // 初始化图表
  useEffect(() => {
    console.log('🔍 地图初始化useEffect触发，条件检查:', {
      hasChartRef: !!chartRef.current,
      hasMapData: !!mapData,
      mapDataFeatures: mapData?.features?.length,
      isLoading,
      shouldInit: !(!chartRef.current || !mapData || isLoading)
    });

    if (!chartRef.current || !mapData || isLoading) {
      console.log('⏸️ 地图初始化跳过，原因:', {
        noChartRef: !chartRef.current,
        noMapData: !mapData,
        stillLoading: isLoading
      });
      return;
    }

    console.log('🚀 开始初始化地图...');

    const initChart = async () => {
      try {
        // 清理现有实例
        if (chartInstance.current) {
          chartInstance.current.dispose();
          chartInstance.current = null;
        }

        // 注册地图数据
        echarts.registerMap('china', mapData);
        
        // 创建图表实例
        chartInstance.current = echarts.init(chartRef.current);
        
        // 基础配置
        const option = {
          geo: {
            map: 'china',
            roam: false, // 完全禁用拖拽和缩放
            // 使用layoutCenter和layoutSize确保完整显示
            layoutCenter: layoutCenter,
            layoutSize: layoutSize,
            // 备用：使用zoom和center
            zoom: zoom,
            center: center,
            itemStyle: {
              areaColor: '#e3f2fd',  // 默认浅蓝色
              borderColor: '#2196f3',
              borderWidth: borderWidth // 使用响应式边框宽度
            },
            emphasis: {
              itemStyle: {
                areaColor: '#bbdefb',
                borderColor: '#1976d2',
                borderWidth: 2
              }
            },
            label: {
              show: false, // 初始化时默认不显示，通过单独的useEffect更新
              fontSize: 12,
              color: '#333',
              fontWeight: 'bold',
              textBorderColor: '#fff',
              textBorderWidth: 2
            }
          },
          series: [
            {
              type: 'map',
              map: 'china',
              geoIndex: 0,
              data: [],
              label: {
                show: false, // 初始化时默认不显示，通过单独的useEffect更新
                fontSize: 12,
                color: '#333',
                fontWeight: 'bold',
                textBorderColor: '#fff',
                textBorderWidth: 2
              },
              itemStyle: {
                borderColor: '#2196f3',
                borderWidth: 1
              },
              emphasis: {
                itemStyle: {
                  areaColor: '#bbdefb'
                },
                label: {
                  show: true,
                  fontSize: 12,
                  fontWeight: 'bold'
                }
              }
            }
          ],
          tooltip: {
            trigger: 'item',
            formatter: (params: any) => {
              return `<div style="padding: 8px;">
                <div style="font-weight: bold;">${params.name}</div>
                <div style="font-size: 12px; color: #666;">点击查看详情</div>
              </div>`;
            }
          }
        };

        chartInstance.current.setOption(option);
        
        // 验证地图是否正确初始化
        const verifyMapInit = () => {
          if (!chartInstance.current) {
            console.error('❌ 地图初始化失败：chartInstance为null');
            return false;
          }
          
          const currentOption = chartInstance.current.getOption();
          console.log('🔍 地图初始化验证:', {
            hasGeo: !!currentOption.geo,
            hasSeries: !!currentOption.series,
            geoMap: currentOption.geo?.[0]?.map,
            seriesType: currentOption.series?.[0]?.type,
            containerSize: {
              width: chartRef.current?.offsetWidth,
              height: chartRef.current?.offsetHeight
            }
          });
          
          return true;
        };
        
        if (!verifyMapInit()) {
          throw new Error('地图初始化验证失败');
        }
        
        // 地图初始化完成，设置就绪状态
        setIsMapReady(true);
        console.log('✅ 地图初始化完成，设置就绪状态');
        
        // 不要在这里立即锁定配置，等待autoAdjustMapToFit完成后再锁定
        console.log('⏳ 等待地图自动调整完成后再锁定配置');
        
        // 地图现在完全固定，移除所有拖拽和缩放事件监听
        // chartInstance.current.on('georoam', ...) 已移除
        
        // 监听点击事件
        chartInstance.current.on('click', (params: any) => {
          const mapProvinceName = params.name;
          const province = findMatchingProvince(mapProvinceName, provinces);
          if (province && onProvinceClick) {
            onProvinceClick(province.id);
          }
        });

        // 监听窗口大小变化
        const handleResize = () => {
          if (chartInstance.current) {
            smartResize(true, '窗口大小变化'); // 保护地图配置
          }
        };
        window.addEventListener('resize', handleResize);

        console.log('图表初始化完成');

        // 强制显示地图 - 确保地图可见
        setTimeout(() => {
          if (chartInstance.current) {
            console.log('🔄 强制刷新地图显示');
            smartResize(false, '初始化完成后强制刷新'); // 初始化阶段不保护配置
            
            // 如果没有省份数据，显示默认地图
            if (!provinces || provinces.length === 0) {
              const defaultData = mapData.features.map((feature: any) => ({
                name: feature.properties.name,
                value: 0,
                itemStyle: {
                  areaColor: '#e3f2fd',
                  borderColor: '#2196f3',
                  borderWidth: 1
                }
              }));
              
              chartInstance.current.setOption({
                series: [{ data: defaultData }]
              });
              console.log('✅ 强制显示默认地图完成');
            }
          }
        }, 200);

        return () => {
          window.removeEventListener('resize', handleResize);
        };
        
      } catch (err) {
        console.error('初始化图表失败:', err);
        setError('图表初始化失败: ' + (err as Error).message);
      }
    };

    initChart();
  }, [mapData, isLoading]); // 保持简单的依赖

  // 添加强制重试机制 - 当DOM准备好后重新尝试初始化
  useEffect(() => {
    if (!chartRef.current || !mapData || isLoading || chartInstance.current) {
      return; // 如果已经初始化过了就不重复
    }

    console.log('🔄 DOM准备好后重新尝试地图初始化');
    
    // 延迟一点确保DOM完全稳定
    const retryTimer = setTimeout(() => {
      if (chartRef.current && mapData && !isLoading && !chartInstance.current) {
        console.log('🎯 重试地图初始化');
        // 手动触发初始化
        const initChart = async () => {
          try {
            console.log('🚀 重试：开始初始化地图...');
            
            // 注册地图数据
            echarts.registerMap('china', mapData);
            
            // 创建图表实例
            chartInstance.current = echarts.init(chartRef.current);
            
            if (!chartInstance.current) {
              throw new Error('ECharts实例创建失败');
            }

            // 简化的基础配置
            const option = {
              geo: {
                map: 'china',
                roam: false, // 完全禁用拖拽和缩放
                // 使用layoutCenter和layoutSize确保完整显示
                layoutCenter: layoutCenter,
                layoutSize: layoutSize,
                // 备用：使用zoom和center
                zoom: zoom,
                center: center,
                itemStyle: {
                  areaColor: '#e3f2fd',
                  borderColor: '#2196f3',
                  borderWidth: borderWidth // 使用响应式边框宽度
                },
                label: { 
                  show: containerSize.width > 768, // 根据容器宽度决定是否显示标签
                  fontSize: fontSize // 使用响应式字体大小
                }
              },
              series: [{
                type: 'map',
                map: 'china',
                geoIndex: 0,
                data: mapData.features.map((feature: any) => ({
                  name: feature.properties.name,
                  value: 0,
                  itemStyle: {
                    areaColor: '#e3f2fd',
                    borderColor: '#2196f3',
                    borderWidth: 1
                  }
                })),
                itemStyle: {
                  borderColor: '#2196f3',
                  borderWidth: 1
                },
                label: { show: false }
              }]
            };

            chartInstance.current.setOption(option);
            console.log('✅ 重试地图初始化成功');
            
            // 设置地图就绪状态
            setIsMapReady(true);
            console.log('✅ 重试初始化完成，设置就绪状态');
            
            // 等待可能的自动调整完成后再锁定配置
            setTimeout(async () => {
              if (chartInstance.current) {
                console.log('🔍 重试初始化后检查边界...');
                const isInBounds = checkMapBounds(chartInstance.current);
                
                if (!isInBounds) {
                  console.log('⚠️ 重试初始化后需要自动调整...');
                  const adjusted = await autoAdjustMapToFit(chartInstance.current);
                  if (adjusted) {
                    console.log('✅ 重试初始化后自动调整完成');
                  }
                }
                
                // 无论是否调整，都锁定最终配置
                setTimeout(() => {
                  lockMapConfiguration();
                  console.log('🔒 重试初始化流程完成，配置已锁定');
                }, 200);
              }
            }, 600);
            
          } catch (err) {
            console.error('❌ 重试地图初始化失败:', err);
          }
        };
        
        initChart();
      }
    }, 500);

    return () => clearTimeout(retryTimer);
  }, [mapData, isLoading]); // 当数据准备好时重试

  // 专门处理省名显示切换
  useEffect(() => {
    if (!chartInstance.current) {
      return;
    }

    try {
      console.log(`🏷️ 切换省名显示: ${showProvinceNames ? '显示' : '隐藏'}`);
      
      // 更新地图配置中的label显示
      const option = {
        geo: {
          label: {
            show: showProvinceNames,
            fontSize: 12,
            color: '#333',
            fontWeight: 'bold',
            textBorderColor: '#fff',
            textBorderWidth: 2
          }
        },
        series: [{
          label: {
            show: showProvinceNames,
            fontSize: 12,
            color: '#333',
            fontWeight: 'bold',
            textBorderColor: '#fff',
            textBorderWidth: 2
          }
        }]
      };

      chartInstance.current.setOption(option);
      
    } catch (err) {
      console.error('❌ 更新省名显示失败:', err);
    }
  }, [showProvinceNames]);

  // 监听缩放状态变化并更新地图
  useEffect(() => {
    if (!chartInstance.current) {
      return;
    }

    try {
      console.log('🔍 更新地图省名显示状态'); // 移除缩放和中心点日志，因为地图固定
      
      // 更新地图的省名显示状态（地图位置固定）
      const option = {
        geo: {
          // 地图位置固定，不需要动态更新
          label: {
            show: showProvinceNames
          }
        },
        series: [{
          label: {
            show: showProvinceNames
          }
        }]
      };

      chartInstance.current.setOption(option);
      
    } catch (err) {
      console.error('❌ 更新地图缩放失败:', err);
    }
  }, []); // 移除mapZoom和mapCenter依赖，因为地图现在完全固定

  // 核心功能：根据省份状态更新地图颜色
  useEffect(() => {
    if (!chartInstance.current || !mapData || !isMapReady) {
      console.log('⚠️ 地图更新跳过 - 缺少必要条件:', { 
        hasChartInstance: !!chartInstance.current, 
        hasMapData: !!mapData,
        isMapReady
      });
      return;
    }

    // 检查地图容器是否可见
    const checkMapVisibility = () => {
      if (!chartRef.current) return false;
      const rect = chartRef.current.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    if (!checkMapVisibility()) {
      console.log('⚠️ 地图容器不可见，跳过颜色更新');
      return;
    }

    try {
      console.log('🎨 更新地图颜色状态 - 完成:', completedProvinces.length, '/', provinces.length);
      console.log('🎮 游戏状态:', { isGameStarted, hasMapData: !!mapData, hasChartInstance: !!chartInstance.current });

      // 如果没有provinces数据（游戏未开始或重置后），显示默认地图
      if (!provinces || provinces.length === 0) {
        console.log('🗺️ 显示默认地图（无游戏数据）');
        
        const defaultSeriesData = mapData.features.map((feature: any) => ({
          name: feature.properties.name,
          value: 0,
          itemStyle: {
            areaColor: '#e3f2fd',  // 默认浅蓝色
            borderColor: '#2196f3',
            borderWidth: 1
          },
          label: {
            show: false,
            color: '#333',
            fontWeight: 'bold',
            fontSize: 11
          }
        }));

        const defaultOption = {
          series: [{
            data: defaultSeriesData
          }]
        };

        chartInstance.current.setOption(defaultOption, false, false); // 不要清空其他配置
        console.log('✅ 默认地图显示完成，数据点数量:', defaultSeriesData.length);
        return;
      }

      console.log('🎯 游戏模式地图更新 - provinces:', provinces.map(p => p.name).join(', '));

      // 定义三种状态的颜色 - 与省份状态颜色更新系统保持一致
      const colors = {
        // 待拼图省份 - 红色
        pending: {
          areaColor: '#ffcdd2',      // 红色填充 - 待拼图省份
          borderColor: '#f44336',
          borderWidth: 1
        },
        // 已完成省份 - 绿色
        completed: {
          areaColor: '#c8e6c9',      // 绿色填充 - 已完成省份
          borderColor: '#4caf50',
          borderWidth: 1
        },
        // 非游戏省份 - 浅蓝色
        nonGame: {
          areaColor: '#e3f2fd',      // 蓝色填充 - 非游戏省份
          borderColor: '#2196f3',
          borderWidth: 1
        }
      };

      // 生成地图数据 - 使用强大的省份名称匹配
      const seriesData = mapData.features.map((feature: any) => {
        const mapProvinceName = feature.properties.name;
        const province = findMatchingProvince(mapProvinceName, provinces);
        
        let style;
        let showLabel = false;
        
        if (province && isGameStarted) {
          // 这是游戏中的省份
          const isCompleted = completedProvinces.includes(province.id);
          if (isCompleted) {
            style = colors.completed;
            showLabel = true; // 已完成的省份显示名称
          } else {
            style = colors.pending;
          }
        } else {
          // 非游戏省份
          style = colors.nonGame;
        }

        return {
          name: mapProvinceName,
          value: province ? (completedProvinces.includes(province.id) ? 1 : 0) : -1,
          itemStyle: {
            areaColor: style.areaColor,
            borderColor: style.borderColor,
            borderWidth: style.borderWidth
          },
          // 尝试在顶层也设置样式
          areaColor: style.areaColor,
          borderColor: style.borderColor,
          borderWidth: style.borderWidth,
          label: {
            show: showLabel,
            color: '#333',
            fontWeight: 'bold',
            fontSize: 11
          }
        };
      });

      // 更新地图
      // 🔑 关键修复：根据锁定状态选择正确的geo配置，确保颜色更新生效
      let geoConfig: any;
      
      if (mapConfigLocked && lockedMapConfig.current) {
        // 地图已锁定，使用锁定的配置
        geoConfig = {
          map: 'china',
          roam: false,
          zoom: lockedMapConfig.current.zoom,
          center: lockedMapConfig.current.center,
          layoutSize: lockedMapConfig.current.layoutSize,
          layoutCenter: lockedMapConfig.current.layoutCenter,
          label: { show: showProvinceNames },
          // 保持其他锁定的配置
          // itemStyle: lockedMapConfig.current.fullGeoConfig?.itemStyle,
          // emphasis: lockedMapConfig.current.fullGeoConfig?.emphasis
        };
        console.log('🔒 颜色更新使用锁定的geo配置:', {
          layoutSize: lockedMapConfig.current.layoutSize,
          zoom: lockedMapConfig.current.zoom,
          center: lockedMapConfig.current.center
        });
      } else {
        // 地图未锁定，使用响应式配置
        geoConfig = {
          map: 'china',
          roam: false,
          layoutCenter: layoutCenter,
          layoutSize: layoutSize,
          zoom: zoom,
          center: center,
          label: { show: showProvinceNames }
        };
        console.log('🔄 颜色更新使用响应式geo配置:', {
          layoutSize: layoutSize,
          zoom: zoom,
          center: center
        });
      }

      // 更新地图series数据 - 始终包含geo配置确保颜色生效
      const updateOption = {
        geo: geoConfig,
        // 添加visualMap来控制颜色
        visualMap: {
          show: false, // 隐藏visualMap控件
          type: 'piecewise',
          pieces: [
            { value: -1, color: '#e3f2fd' }, // 非游戏省份 - 蓝色
            { value: 0, color: '#ffcdd2' },  // 待拼图省份 - 红色
            { value: 1, color: '#c8e6c9' }   // 已完成省份 - 绿色
          ],
          dimension: 0 // 使用value字段
        },
        series: [{
          type: 'map',
          map: 'china',
          geoIndex: 0,
          data: seriesData,
          // 添加全局itemStyle作为基础样式
          itemStyle: {
            areaColor: '#e3f2fd', // 默认蓝色
            borderColor: '#2196f3',
            borderWidth: 1
          },
          label: { show: false },
          emphasis: {
            itemStyle: {
              areaColor: '#bbdefb'
            }
          }
        }]
      };

      console.log('🎨 准备更新地图颜色，数据点数量:', seriesData.length);
      console.log('🎨 颜色数据样本:', seriesData.slice(0, 3).map(item => ({
        name: item.name,
        areaColor: item.itemStyle?.areaColor,
        borderColor: item.itemStyle?.borderColor
      })));

      // 在setOption之前检查当前配置
      const beforeOption = chartInstance.current.getOption();
      console.log('🔍 setOption前的配置:', {
        hasGeo: !!beforeOption.geo,
        hasSeries: !!beforeOption.series,
        seriesDataLength: beforeOption.series?.[0]?.data?.length
      });

      chartInstance.current.setOption(updateOption, true); // 使用merge模式
      
      // 立即检查setOption后的配置
      const immediateAfterOption = chartInstance.current.getOption();
      console.log('🔍 setOption后立即检查:', {
        hasGeo: !!immediateAfterOption.geo,
        hasSeries: !!immediateAfterOption.series,
        seriesDataLength: immediateAfterOption.series?.[0]?.data?.length,
        firstThreeItems: immediateAfterOption.series?.[0]?.data?.slice(0, 3).map((item: any) => ({
          name: item.name,
          value: item.value,
          hasItemStyle: !!item.itemStyle,
          areaColor: item.itemStyle?.areaColor,
          borderColor: item.itemStyle?.borderColor
        }))
      });
      
      // 强制刷新地图显示
      setTimeout(() => {
        if (chartInstance.current) {
          smartResize(true, '颜色更新完成'); // 保护地图配置
          
          // 验证更新是否生效
          const afterOption = chartInstance.current.getOption();
          console.log('🔍 颜色更新后验证:', {
            hasGeo: !!afterOption.geo,
            hasSeries: !!afterOption.series,
            seriesDataLength: afterOption.series?.[0]?.data?.length,
            sampleColors: afterOption.series?.[0]?.data?.slice(0, 3).map((item: any) => ({
              name: item.name,
              areaColor: item.itemStyle?.areaColor
            }))
          });
        }
      }, 100);
      
      console.log('✅ 地图颜色更新完成，数据点数量:', seriesData.length);
      
    } catch (err) {
      console.error('❌ 更新地图颜色失败:', err);
    }
  }, [provinces, completedProvinces, mapData, isGameStarted, isMapReady]);

  // 专门处理游戏开始时的地图显示
  useEffect(() => {
    if (!chartInstance.current || !mapData || !isGameStarted || !isMapReady) {
      return;
    }

    console.log('🎮 游戏开始，确保地图显示正确的颜色状态');
    
    // 延迟一点时间确保DOM稳定，然后强制触发颜色更新
    const timer = setTimeout(() => {
      if (chartInstance.current && isMapReady) {
        console.log('🔄 游戏开始后强制刷新地图颜色');
        smartResize(true, '游戏开始后刷新'); // 保护地图配置
        
        // 如果有省份数据，确保颜色状态正确
        if (provinces && provinces.length > 0) {
          console.log('🎨 游戏开始时强制应用颜色状态，省份数量:', provinces.length);
          // 颜色更新逻辑会由主要的useEffect处理，这里只是确保触发
        }
        
        console.log('✅ 游戏开始地图颜色刷新完成');
      }
    }, 200); // 延迟200ms确保所有状态都已更新

    return () => clearTimeout(timer);
  }, [isGameStarted, isMapReady, provinces.length]);

  // 🎨 省份状态颜色更新 - 监听游戏状态变化
  useEffect(() => {
    // 临时禁用此系统，避免与主要颜色更新系统冲突
    console.log('⚠️ 省份状态颜色更新系统已禁用，避免冲突');
    return;
    
    if (!chartInstance.current || !mapData || !isGameStarted) {
      return;
    }

    console.log('🎨 更新省份状态颜色', {
      provincesCount: provinces?.length || 0,
      completedCount: completedProvinces?.length || 0,
      isGameStarted
    });

    try {
      // 定义省份状态颜色
      const colors = {
        pending: {
          areaColor: '#ffcdd2',      // 红色填充 - 待拼图省份
          borderColor: '#f44336',
          borderWidth: 1
        },
        completed: {
          areaColor: '#c8e6c9',      // 绿色填充 - 已完成省份
          borderColor: '#4caf50',
          borderWidth: 1
        },
        nonGame: {
          areaColor: '#e3f2fd',      // 蓝色填充 - 非游戏省份
          borderColor: '#2196f3',
          borderWidth: 1
        },
        special: {
          areaColor: '#fff3e0',      // 橙色填充 - 港澳特殊区域
          borderColor: '#ff9800',
          borderWidth: 1
        }
      };

      // 生成更新的地图数据
      const updatedSeriesData = mapData.features.map((feature: any) => {
        const mapProvinceName = feature.properties.name;
        
        // 查找匹配的游戏省份
        let matchedProvince = null;
        if (provinces && provinces.length > 0) {
          matchedProvince = findMatchingProvince(mapProvinceName, provinces);
        }
        
        let style = colors.nonGame;
        let showLabel = false;
        let labelText = '';
        
        // 香港澳门特殊处理
        if (mapProvinceName === '香港特别行政区' || mapProvinceName === '澳门特别行政区') {
          style = colors.special;
          showLabel = true;
          labelText = mapProvinceName === '香港特别行政区' ? '港' : '澳';
          console.log(`🏷️ 特殊区域: ${mapProvinceName} -> ${labelText}`);
        } else if (matchedProvince && isGameStarted) {
          // 游戏中的省份
          if (completedProvinces && completedProvinces.includes(matchedProvince.id)) {
            style = colors.completed;
            showLabel = showProvinceNames;
            labelText = matchedProvince.shortName || matchedProvince.name;
            console.log(`✅ 已完成: ${mapProvinceName} -> ${labelText}`);
          } else {
            style = colors.pending;
            console.log(`⏳ 待拼图: ${mapProvinceName}`);
          }
        } else {
          console.log(`🔵 非游戏: ${mapProvinceName}`);
        }

        return {
          name: mapProvinceName,
          value: 0,
          itemStyle: {
            areaColor: style.areaColor,
            borderColor: style.borderColor,
            borderWidth: style.borderWidth
          },
          label: {
            show: showLabel,
            color: '#333',
            fontWeight: 'bold',
            fontSize: Math.max(fontSize - 2, 8),
            formatter: labelText
          }
        };
      });

      // 更新地图series数据 - 使用完整配置确保颜色生效，同时保留geo配置
      const updateOption = {
        geo: {
          map: 'china',
          roam: false,
          layoutCenter: layoutCenter,
          layoutSize: layoutSize,
          zoom: zoom,
          center: center,
          label: { show: showProvinceNames }
        },
        series: [{
          type: 'map',
          map: 'china',
          geoIndex: 0,
          data: updatedSeriesData,
          // 重要：不设置全局itemStyle，让每个数据项的itemStyle生效
          label: { show: false },
          emphasis: {
            itemStyle: {
              areaColor: '#bbdefb'
            }
          }
        }]
      };

      // 异步更新避免渲染冲突，使用merge模式
      setTimeout(() => {
        if (chartInstance.current) {
          // 设置更新标志，防止resize冲突
          isUpdatingColors.current = true;
          
          // 重要：确保地图数据已注册
          echarts.registerMap('china', mapData);
          
          // 重要：使用true参数进行合并更新
          chartInstance.current.setOption(updateOption, true);
          
          // 强制重新渲染
          setTimeout(() => {
            if (chartInstance.current) {
              smartResize(true, '省份状态颜色更新'); // 保护地图配置
            }
            // 清除更新标志
            isUpdatingColors.current = false;
          }, 50);
          
          console.log('✅ 省份状态颜色更新完成', {
            totalProvinces: updatedSeriesData.length,
            pendingCount: updatedSeriesData.filter(p => p.itemStyle.areaColor === colors.pending.areaColor).length,
            completedCount: updatedSeriesData.filter(p => p.itemStyle.areaColor === colors.completed.areaColor).length,
            specialCount: updatedSeriesData.filter(p => p.itemStyle.areaColor === colors.special.areaColor).length
          });
          
          // 调试：输出前几个省份的颜色数据
          console.log('🔍 颜色数据样本:', updatedSeriesData.slice(0, 5).map(item => ({
            name: item.name,
            areaColor: item.itemStyle.areaColor,
            borderColor: item.itemStyle.borderColor
          })));
          
          // 调试：检查ECharts当前配置
          const currentOption = chartInstance.current.getOption();
          console.log('🔍 当前ECharts配置:', {
            hasGeo: !!currentOption.geo,
            hasSeries: !!currentOption.series,
            seriesLength: currentOption.series?.length || 0,
            seriesDataLength: currentOption.series?.[0]?.data?.length || 0
          });
        }
      }, 0);

    } catch (error) {
      console.error('❌ 更新省份状态颜色失败:', error);
    }
  }, [provinces, completedProvinces, isGameStarted, showProvinceNames, fontSize, mapData]);

  // 容器尺寸变化时更新地图配置
  useEffect(() => {
    if (!chartInstance.current || containerSize.width === 0 || containerSize.height === 0) return;
    
    try {
      console.log('📐 容器尺寸变化，调整地图尺寸:', {
        containerSize,
        layoutSize,
        layoutCenter,
        zoom,
        center
      });
      
      // 只调整ECharts尺寸，避免geo配置冲突
      setTimeout(() => {
        if (chartInstance.current) {
          smartResize(true, '容器尺寸变化'); // 保护地图配置
          console.log('✅ 地图尺寸调整完成');
        }
      }, 100);
      
    } catch (error) {
      console.error('地图尺寸调整失败:', error);
    }
  }, [containerSize, layoutSize, layoutCenter, zoom, center, borderWidth, fontSize]);

  // 响应式调整监听
  useEffect(() => {
    if (!chartRef.current || !chartInstance.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // 避免在颜色更新期间进行resize操作
        if (isUpdatingColors.current) {
          console.log('⏸️ 跳过resize操作，颜色更新进行中');
          return;
        }
        
        // 避免在拖拽期间进行resize操作
        if (isDragging.current) {
          console.log('⏸️ 跳过resize操作，拖拽进行中');
          return;
        }
        
        try {
          // 只调整ECharts尺寸，不更新geo配置避免与series颜色控制冲突
          smartResize(true, 'ResizeObserver触发'); // 保护地图配置
          console.log('📏 地图尺寸调整完成');
        } catch (error) {
          console.error('地图响应式调整失败:', error);
        }
      }
    });

    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []); // 简化依赖项，避免频繁重新创建

  // 清理函数
  useEffect(() => {
    return () => {
      console.log('🧹 开始清理ECharts实例和事件监听器');
      
      try {
        // 清理ECharts实例
        if (chartInstance.current) {
          console.log('🗑️ 销毁ECharts实例');
          chartInstance.current.dispose();
          chartInstance.current = null;
        }
        
        // 清理DOM引用
        if (chartRef.current) {
          console.log('🧹 清理DOM引用');
          // 不要手动操作DOM，让React处理
          // chartRef.current = null; // 不要手动设置为null
        }
        
        console.log('✅ ECharts清理完成');
      } catch (error) {
        console.error('❌ ECharts清理失败:', error);
        // 即使清理失败也要重置引用
        chartInstance.current = null;
      }
    };
  }, []);

  // 处理拖拽放置
  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    
    // 设置拖拽状态，防止resize操作干扰
    isDragging.current = true;
    
    setDragHint({ show: false, message: '', type: 'info' });
    setMousePosition(null);
    
    const provinceId = event.dataTransfer.getData('text/plain');
    if (!provinceId || !chartInstance.current || !mapData) {
      toast.error('拖拽数据无效，请重试', {
        duration: 2000,
        icon: '❌'
      });
      return;
    }

    const rect = chartRef.current?.getBoundingClientRect();
    if (!rect) {
      toast.error('无法获取地图位置信息', {
        duration: 2000,
        icon: '❌'
      });
      return;
    }

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    try {
      // 添加详细的调试信息
      console.log('🔍 Drop操作调试信息:', {
        hasChartInstance: !!chartInstance.current,
        hasMapData: !!mapData,
        mousePosition: { x, y },
        provinceId
      });
      
      // 检查ECharts实例状态
      if (chartInstance.current) {
        const currentOption = chartInstance.current.getOption();
        console.log('🔍 ECharts配置状态:', {
          hasGeo: !!currentOption.geo,
          geoType: typeof currentOption.geo,
          geoIsArray: Array.isArray(currentOption.geo),
          geoMap: currentOption.geo?.map || (Array.isArray(currentOption.geo) ? currentOption.geo[0]?.map : null),
          hasSeries: !!currentOption.series,
          seriesLength: currentOption.series?.length || 0
        });
        
        // 尝试检查坐标系统是否真的可用
        try {
          const testPoint = chartInstance.current.convertFromPixel('geo', [100, 100]);
          console.log('🔍 坐标系统测试成功:', { testPoint });
        } catch (testErr) {
          console.warn('⚠️ 坐标系统测试失败:', testErr);
        }
      }
      
      // 坐标转换，带重试机制处理resize后的时序问题
      let geoPoint;
      try {
        geoPoint = chartInstance.current.convertFromPixel('geo', [x, y]);
        console.log('🔍 坐标转换结果:', { geoPoint, x, y, isValid: geoPoint && geoPoint.length >= 2 });
      } catch (convertError) {
        console.warn('⚠️ 首次坐标转换失败，可能是resize操作影响，尝试重试:', convertError);
        // 等待一小段时间后重试，让坐标系统稳定
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
          geoPoint = chartInstance.current.convertFromPixel('geo', [x, y]);
          console.log('✅ 重试坐标转换结果:', { geoPoint, x, y, isValid: geoPoint && geoPoint.length >= 2 });
        } catch (retryError) {
          console.error('❌ 重试坐标转换仍然失败:', retryError);
          throw retryError;
        }
      }
      
      // 检查坐标转换结果的有效性
      if (!geoPoint || geoPoint.length < 2 || geoPoint[0] === undefined || geoPoint[1] === undefined) {
        console.warn('⚠️ 坐标转换返回无效结果:', { 
          geoPoint, 
          mousePos: { x, y },
          containerSize: { 
            width: chartRef.current?.offsetWidth, 
            height: chartRef.current?.offsetHeight 
          }
        });
        toast.error('请拖拽到地图区域内', {
          duration: 2500,
          icon: '🎯'
        });
        return;
      }

      // 查找目标省份
      const targetProvince = provinces.find(p => p.id === provinceId);
      if (!targetProvince) {
        toast.error('找不到对应的省份信息', {
          duration: 2000,
          icon: '❌'
        });
        return;
      }

      // 查找对应的地图特征
      const targetFeature = mapData.features.find((feature: any) => {
        const mapProvinceName = feature.properties.name;
        const matchedProvince = findMatchingProvince(mapProvinceName, provinces);
        return matchedProvince && matchedProvince.id === targetProvince.id;
      });
      
      if (!targetFeature) {
        toast.error(`找不到${targetProvince.name}在地图上的对应区域`, {
          duration: 2500,
          icon: '❌'
        });
        return;
      }

      // 检查是否在正确的省份区域内
      const isInCorrectArea = isPointInGeometry([geoPoint[0], geoPoint[1]], targetFeature.geometry);
      
      if (isInCorrectArea) {
        // 正确放置
        const center = getProvinceCenter(targetFeature);
        placeProvince(provinceId, { x: center[0], y: center[1] });
        toast.success(`🎉 ${targetProvince.name} 放置正确！`, {
          duration: 2000,
          icon: '✅',
          style: {
            background: '#10B981',
            color: 'white',
          }
        });
      } else {
        // 检查是否接近正确区域（容错处理）
        const center = getProvinceCenter(targetFeature);
        const distance = calculateDistance([geoPoint[0], geoPoint[1]], center);
        
        // 动态容错范围 - 根据省份大小调整
        const tolerance = getProvinceTolerance(targetProvince.name);
        console.log(`🎯 ${targetProvince.name} 容错检查: 距离=${distance.toFixed(2)}, 容错范围=${tolerance}`);
        
        if (distance <= tolerance) {
          placeProvince(provinceId, { x: center[0], y: center[1] });
          toast.success(`🎯 ${targetProvince.name} 放置正确！`, {
            duration: 2000,
            icon: '✅',
            style: {
              background: '#10B981',
              color: 'white',
            }
          });
        } else {
          // 提供具体的错误提示
          const distanceKm = Math.round(distance * 100); // 大概的距离
          
          // 检查是否拖拽到了其他省份区域
          const droppedOnFeature = mapData.features.find((feature: any) => {
            return isPointInGeometry([geoPoint[0], geoPoint[1]], feature.geometry);
          });
          
          if (droppedOnFeature) {
            const droppedProvinceName = droppedOnFeature.properties.name;
            toast.error(`❌ ${targetProvince.name} 不应该放在 ${droppedProvinceName} 的位置`, {
              duration: 3500,
              icon: '🎯',
              style: {
                background: '#EF4444',
                color: 'white',
              }
            });
          } else {
            toast.error(`❌ ${targetProvince.name} 位置不正确`, {
              duration: 3000,
              icon: '🎯',
              style: {
                background: '#EF4444',
                color: 'white',
              }
            });
          }
          
          // 显示距离提示（如果距离不是太远）
          if (distanceKm < 500) {
            setTimeout(() => {
              toast(`💡 提示：${targetProvince.name} 的正确位置距离这里约 ${distanceKm}km`, {
                duration: 2500,
                icon: '📍',
                style: {
                  background: '#3B82F6',
                  color: 'white',
                }
              });
            }, 800);
          }
        }
      }
    } catch (err) {
      console.error('处理拖拽放置失败:', err);
      
      // 检查是否是坐标系统相关的错误
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.includes('coordinate system') || errorMessage.includes('convertFromPixel')) {
        console.warn('⚠️ 坐标转换失败，可能地图正在初始化');
        toast.error('地图正在初始化，请稍后再试', {
          duration: 2000,
          icon: '⏳'
        });
      } else {
        toast.error('拖拽处理失败，请重试', {
          duration: 2000,
          icon: '❌'
        });
      }
    } finally {
      // 无论成功还是失败，都要清除拖拽状态
      isDragging.current = false;
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    
    // 设置拖拽状态，防止resize操作干扰
    isDragging.current = true;
    
    // 更新鼠标位置（相对于页面）
    setMousePosition({ x: event.clientX, y: event.clientY });
    
    // 添加拖拽悬停的视觉反馈
    const rect = chartRef.current?.getBoundingClientRect();
    if (!rect || !chartInstance.current || !mapData) {
      setDragHint({
        show: true,
        message: '拖拽到地图区域内',
        type: 'info'
      });
      return;
    }

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    try {
      // 直接尝试坐标转换，如果失败则在catch中处理
      const geoPoint = chartInstance.current.convertFromPixel('geo', [x, y]);
      if (!geoPoint || geoPoint.length < 2) {
        setDragHint({
          show: true,
          message: '请拖拽到地图区域内',
          type: 'warning'
        });
        return;
      }

      // 检查当前悬停在哪个省份上
      const hoveredFeature = mapData.features.find((feature: any) => {
        return isPointInGeometry([geoPoint[0], geoPoint[1]], feature.geometry);
      });

      if (hoveredFeature) {
        const hoveredProvinceName = hoveredFeature.properties.name;
        
        // 获取当前拖拽的省份ID
        const provinceId = event.dataTransfer.getData('text/plain');
        const targetProvince = provinces.find(p => p.id === provinceId);
        
        if (targetProvince) {
          // 检查是否是正确的省份
          const matchedProvince = findMatchingProvince(hoveredProvinceName, provinces);
          const isCorrectProvince = matchedProvince && matchedProvince.id === targetProvince.id;
          
          if (isCorrectProvince) {
            // 计算精度预览
            const targetFeature = mapData.features.find((feature: any) => {
              const mapProvinceName = feature.properties.name;
              const matched = findMatchingProvince(mapProvinceName, provinces);
              return matched && matched.id === targetProvince.id;
            });
            
            if (targetFeature) {
              const center = getProvinceCenter(targetFeature);
              const distance = calculateDistance([geoPoint[0], geoPoint[1]], center);
              const tolerance = getProvinceTolerance(targetProvince.name);
              const isInExactArea = isPointInGeometry([geoPoint[0], geoPoint[1]], targetFeature.geometry);
              const accuracy = calculateAccuracy(distance, tolerance, isInExactArea);
              
              // 显示精度指示器
              setAccuracyIndicator({
                show: true,
                accuracy,
                distance,
                provinceName: targetProvince.name
              });
            }
            
            setDragHint({
              show: true,
              message: `✓ ${targetProvince.name}`,
              type: 'success'
            });
          } else {
            // 隐藏精度指示器
            setAccuracyIndicator({ show: false, accuracy: 'perfect' });
            
            setDragHint({
              show: true,
              message: `${hoveredProvinceName} ≠ ${targetProvince.name}`,
              type: 'error'
            });
          }
        } else {
          // 隐藏精度指示器
          setAccuracyIndicator({ show: false, accuracy: 'perfect' });
          
          setDragHint({
            show: true,
            message: `当前：${hoveredProvinceName}`,
            type: 'info'
          });
        }
      } else {
        setDragHint({
          show: true,
          message: '拖拽到省份区域',
          type: 'info'
        });
      }
    } catch (err) {
      console.error('处理拖拽悬停失败:', err);
      
      // 检查是否是坐标系统相关的错误
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.includes('coordinate system') || errorMessage.includes('convertFromPixel')) {
        console.warn('⚠️ 坐标转换失败，可能地图正在初始化');
        setDragHint({
          show: true,
          message: '地图正在初始化...',
          type: 'info'
        });
      } else {
        setDragHint({
          show: true,
          message: '拖拽到正确位置',
          type: 'info'
        });
      }
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    // 清除拖拽状态
    isDragging.current = false;
    
    setDragHint({ show: false, message: '', type: 'info' });
    setAccuracyIndicator({ show: false, accuracy: 'perfect' });
    setMousePosition(null);
  };

  // 加载状态
  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded-lg min-h-[700px] lg:min-h-[800px]">
        <div className="text-center">
          <motion.div
            className="text-4xl mb-4"
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            🌍
          </motion.div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            正在加载地图数据...
          </h3>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-red-50 rounded-lg min-h-[700px] lg:min-h-[800px]">
        <div className="text-center">
          <div className="text-4xl mb-4">❌</div>
          <h3 className="text-lg font-semibold text-red-700 mb-2">
            地图加载失败
          </h3>
          <p className="text-red-600 text-sm mb-4">
            {error}
          </p>
          <button
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            onClick={() => window.location.reload()}
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  // 游戏未开始状态
  if (!isGameStarted) {
    return (
      <div 
        className="w-full h-full flex items-center justify-center bg-gray-50 rounded-lg"
        style={{ minHeight: `${containerHeight}px` }}
      >
        <div className="text-center">
          <div className="text-6xl mb-4">🗺️</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            中国地图拼图游戏
          </h3>
          <p className="text-gray-500 mb-2">
            基于阿里云真实省级地图数据
          </p>
          <p className="text-sm text-gray-400">
            包含 {mapData?.features?.length || 0} 个省级行政区
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {/* ECharts地图容器 - 响应式高度 */}
      <div
        ref={chartRef}
        className="w-full h-full relative"
        style={{ minHeight: `${containerHeight}px` }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {/* 地图现在完全固定，移除重置按钮 */}
        {/* 重置按钮已移除，因为地图位置和大小固定不变 */}

        {/* 地图现在完全固定，移除所有缩放和拖拽控件 */}
        
        {/* 轻量级拖拽提示 - 仅跟随鼠标 */}
        <DragDropHint
          isVisible={dragHint.show}
          message={dragHint.message}
          type={dragHint.type}
          position={mousePosition}
        />
        
        {/* 精度指示器 */}
        <AccuracyIndicator
          accuracy={accuracyIndicator.accuracy}
          distance={accuracyIndicator.distance}
          provinceName={accuracyIndicator.provinceName}
          isVisible={accuracyIndicator.show}
          position={mousePosition}
        />
      </div>
      
      {/* 简洁的进度指示器 */}
      <div className="absolute top-4 left-4 bg-white rounded-lg shadow-md p-3 border border-gray-200">
        <div className="flex items-center space-x-3">
          <span className="text-sm font-medium text-gray-700">完成进度</span>
          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-red-400 via-yellow-400 to-green-500"
              initial={{ width: 0 }}
              animate={{ width: `${provinces.length > 0 ? (completedProvinces.length / provinces.length) * 100 : 0}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <span className="text-sm font-bold text-gray-800">
            {completedProvinces.length}/{provinces.length}
          </span>
        </div>
      </div>

      {/* 简洁的颜色图例 */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-md p-3 border border-gray-200">
        <div className="text-xs text-gray-700 space-y-2">
          <div className="font-medium text-sm text-gray-800 mb-2">省份状态</div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded border border-red-500" style={{ backgroundColor: '#ffebee' }}></div>
            <span>🎯 待拼图</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded border border-green-500" style={{ backgroundColor: '#e8f5e8' }}></div>
            <span>✅ 已完成</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded border border-blue-500" style={{ backgroundColor: '#e3f2fd' }}></div>
            <span>🌐 其他省份</span>
          </div>
        </div>
      </div>

      {/* 操作提示 */}
      <div className="absolute bottom-4 right-4 text-sm text-gray-500 bg-white bg-opacity-90 px-3 py-2 rounded-lg shadow-sm">
        💡 拖拽右侧省份到地图对应位置
      </div>

      {/* 游戏完成庆祝 */}
      {completedProvinces.length === provinces.length && provinces.length > 0 && (
        <motion.div
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="bg-white rounded-xl p-8 text-center shadow-2xl max-w-md"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          >
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              恭喜完成！
            </h2>
            <p className="text-gray-600 mb-6">
              你已经成功完成了所有省份的拼图！
            </p>
            <div className="text-sm text-gray-500 mb-6">
              完成省份: {completedProvinces.length}/{provinces.length}
            </div>
            
            <div className="flex space-x-3">
              <button
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
                onClick={continueChallenge}
              >
                🚀 继续挑战
              </button>
              <button
                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors"
                onClick={backToHome}
              >
                🏠 返回首页
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};
